import { useState, useEffect, useMemo, useCallback } from "react";
import TableCertificado from "../../Components/TableCertificados";
import { formatDateTime } from "../../Components/jwtUtils";
import Api from "../../Auth/Api";
import { pdf } from "@react-pdf/renderer";
import GerarCertificado from "../../Components/GerarCertificado";
import { useAjustes } from "../../Context/AjustesContext";
import { Toast, Modal } from "react-bootstrap";
import { getCertificadoPdfUrl } from "../../utils/certificadoPdfUrl";

function Certificado() {

    const token = JSON.parse(localStorage.getItem("user_token"));
    const { ajustes } = useAjustes();
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [showModalInfoExpirado, setShowModalInfoExpirado] = useState(false);

    //listar todos os cursos
    const [dataTodosCursos, setDataTodosCursos] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`/api/listar-todos-cursos`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataTodosCursos(response.data);
            } catch (error) {
                console.error(error.response.data.message)
            }
        }
        fetchData();
    }, [token])

    // listar certificados
    const [dataCertificados, setDataCertificados] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`/api/listar-certificados-aluno`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataCertificados(response.data);
            } catch (error) {
                setDataCertificados([]); // Se não tiver dados, limpa a lista
                console.error(error.response.data.message)
            }
        }
        fetchData();
    }, [token])

    // Filtro para seleção por curso
    const [selectCurso, setSelectCurso] = useState(null);
    const FilterDataProvas = selectCurso ? dataCertificados.filter(item => item.idCurso === selectCurso) : dataCertificados;

    // Estado para armazenar os status de expiração dos certificados
    const [certificadosStatus, setCertificadosStatus] = useState({});
    
    // Carregar status de expiração para todos os certificados listados
    useEffect(() => {
        async function carregarStatusExpiracao() {
            if (dataCertificados.length === 0) return;
            
            const status = {};
            for (const cert of dataCertificados) {
                try {
                    const response = await Api.get(`api/verificar-expiracao-certificado/${cert.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    status[cert.id] = response.data;
                } catch (error) {
                    console.error(`Erro ao verificar expiração do certificado ${cert.id}:`, error);
                }
            }
            setCertificadosStatus(status);
        }
        
        carregarStatusExpiracao();
    }, [dataCertificados, token]);

    // Função para verificar expiração e gerar o PDF
    const handleDownloadPDF = useCallback(async (certificado) => {
        try {
            // Verificar se o certificado está expirado
            const verificacaoExpiracao = await Api.get(`api/verificar-expiracao-certificado/${certificado.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Se o certificado está expirado, mostra mensagem e não permite download
            if (verificacaoExpiracao.data.expirado) {
                setToast({
                    header: 'Certificado expirado!',
                    body: "Este certificado expirou. É necessário refazer o curso para obter um novo certificado.",
                    type: 'toast-warning'
                });
                setShowToast(true);
                return;
            }
            
            // Obter informações completas do certificado
            const infoCertificadoResponse = await Api.get(`api/info-certificado/${certificado.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const infoCertificado = infoCertificadoResponse.data[0];

            if (infoCertificado?.arquivo_pdf) {
                const arquivoUrl = getCertificadoPdfUrl(infoCertificado.arquivo_pdf);
                if (arquivoUrl) {
                    window.open(arquivoUrl, '_blank');
                    return;
                }
            }
            let dataParaCertificado = certificado.data;

            // Se for curso misto, usar data da prática se disponível
            if (infoCertificado.tipo === 'misto') {
                if (infoCertificado.data_pratica) {
                    // Se a data da prática já veio na resposta, usar ela
                    dataParaCertificado = infoCertificado.data_pratica;
                } else {
                    // Se não veio, buscar práticas ativas do curso
                    try {
                        const praticasResponse = await Api.get(`api/cursos/${infoCertificado.idCurso}/praticas?arquivadas=false`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        
                        // Filtrar pela prática do usuário específico
                        const praticaUsuario = praticasResponse.data.find(
                            p => p.idUsuario === certificado.idUsuario
                        );
                        
                        if (praticaUsuario && praticaUsuario.data_treinamento) {
                            dataParaCertificado = praticaUsuario.data_treinamento;
                        }
                    } catch (error) {
                        console.error('Erro ao buscar prática:', error);
                        // Se der erro, continua com a data padrão
                    }
                }
            } else {
                // Se não for curso misto, usar data da avaliação se disponível
                if (infoCertificado.data_avaliacao) {
                    dataParaCertificado = infoCertificado.data_avaliacao;
                }
            }
            
            // Obter informações da unidade do usuário
            const userInfo = await Api.get(`api/usuario/${certificado.idUsuario}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Dados para gerar o PDF, usando dados da verificação de expiração e do usuário
            const certificadoData = { 
                id: certificado.id, 
                nomeAluno: certificado.nomeAluno, 
                cpf: infoCertificado.cpf || userInfo.data?.cpf || null,
                nomeCurso: certificado.nomeCurso, 
                prazo_validade: verificacaoExpiracao.data.prazo_validade, 
                data: dataParaCertificado, 
                nomeGestor: certificado.nomeGestor, 
                certificado: certificado.certificado,
                certificado_frente: certificado.certificado_frente, 
                nomeTopico: certificado.nomeTopico, 
                cargaHorariaTotal: certificado.cargaHorariaTotal, 
                unidade: userInfo.data?.unidade || "Não especificada" 
            };
            
            // Gerar o PDF
            const pdfBlob = await pdf(
                <GerarCertificado 
                    data={certificadoData} 
                    diplomaUrl={ajustes?.diploma || null}
                    empresa={ajustes?.titulo || "Empresa"}
                    textoCertificado={ajustes?.texto_certificado || null}
                />
            ).toBlob();
            
            // Criar URL e fazer download
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `certificado_${certificado.nomeAluno}_${certificado.nomeCurso}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (error) {
            console.error("Erro ao gerar o PDF:", error);
            setToast({ 
                header: 'Erro!', 
                body: "Ocorreu um erro ao gerar o certificado. Tente novamente.",
                type: 'toast-error'
            });
            setShowToast(true);
        }
    }, [token, ajustes?.diploma, ajustes?.titulo, ajustes?.texto_certificado]);

    const columns = useMemo(
        () => [
            {
                Header: () => null, // Sem título para esta coluna
                id: 'icon',
                accessor: row => row.id,
                Cell: () => (
                    <div className="d-flex align-items-center justify-content-center">
                        <i className="bi bi-card-checklist fs-3"></i>
                    </div>
                )
            },
            {
                Header: 'ID',
                accessor: 'id',
                Cell: ({ cell: { value } }) => (
                    <div className="text-right">
                        <span className="badge bg-light text-dark border">{value}</span>
                    </div>
                )
            },
            {
                Header: 'Curso',
                accessor: 'nomeCurso'
            },
            {
                Header: 'Gestor',
                accessor: 'nomeGestor',
                Cell: ({ value }) => (
                    value ? value : "Automático"
                )
            },
            {
                Header: () => "Data", // Sem título para esta coluna
                id: 'data',
                accessor: row => row.data,
                Cell: ({ cell: { value }, row: { original } }) => (
                    <div className="small text-center">{formatDateTime(value)}</div>
                )
            },
            {
                Header: () => 'Status',
                id: 'status',
                accessor: row => row.id,
                Cell: ({ cell: { value }, row: { original } }) => {
                    const status = certificadosStatus[value];
                    if (!status) return <div className="text-center"><span className="spinner-border spinner-border-sm" role="status"></span></div>;
                    
                    if (status.prazo_validade === null || status.prazo_validade === 0) {
                        return (
                            <div className="text-center">
                                <span className="badge bg-info">Permanente</span>
                            </div>
                        );
                    }
                    
                    // Expira só pela data: o certificado arquivado na reciclagem vale até vencer
                    if (status.expirado) {
                        return (
                            <div className="text-center">
                                <span className="badge bg-danger">Expirado</span>
                               
                            </div>
                        );
                    }
                    
                    return (
                        <div className="text-center">
                            <span className="badge bg-success">Válido</span>
                            <div className="small text-muted">até {new Date(status.data_expiracao).toLocaleDateString()}</div>
                            {status.arquivado && (
                                <div className="small text-muted" title={status.message}>
                                    {status.substituido ? 'ciclo anterior' : 'em reciclagem'}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: () => null, // Sem título para esta coluna
                id: 'download',
                accessor: row => row.id,
                Cell: ({ cell: { value }, row: { original } }) => {
                    const status = certificadosStatus[value];
                    const isExpirado = status?.expirado;
                    
                    return (
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            {!isExpirado && (
                                <button 
                                    onClick={() => handleDownloadPDF(original)} 
                                    className="btn btn-outline-primary rounded px-2"
                                >
                                    <i className="bi bi-download" />
                                </button>
                            )}
                            {isExpirado && (
                                <button
                                    onClick={() => setShowModalInfoExpirado(true)}
                                    className="btn btn-outline-primary rounded px-2"
                                    title="Por que está expirado?"
                                >
                                    <i className="bi bi-info-circle" />
                                </button>
                            )}
                        </div>
                    );
                }
            }
        ],
        [handleDownloadPDF, certificadosStatus]
    )

    return (
        <>
            <div className="fade-in w-100">
                <span className="text-primary fs-1">Certificados</span>

                <p className="fs-6">
                    Lista com todos os seus certificados disponíveis
                </p>

                <TableCertificado columns={columns} data={FilterDataProvas} filterSelect={dataTodosCursos} search={false} setSelectCurso={setSelectCurso} titulo="Lista de Certificados" />
            </div>
            
            {/* Toast para mostrar mensagens */}
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            {/* Modal de Informação sobre Certificado Expirado */}
            <Modal 
                show={showModalInfoExpirado} 
                onHide={() => setShowModalInfoExpirado(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-semibold fs-5"> 
                        <i className="bi bi-info-circle-fill text-info me-2"></i>
                        Por que este certificado está expirado?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-info">
                        <i className="bi bi-lightbulb-fill me-2"></i>
                        <strong>Informação importante:</strong>
                    </div>
                    <p>
                        Um certificado fica <strong>"expirado"</strong> somente depois que passa a data de validade dele
                        (data de conclusão do treinamento + prazo de validade do curso).
                    </p>
                    <ul>
                        <li className="mb-2">
                            <strong>Reciclagem automática:</strong> 90 dias antes do vencimento, o curso é liberado automaticamente
                            para você refazer. Seu certificado atual continua válido até a data de vencimento.
                        </li>
                        <li className="mb-2">
                            <strong>Conclusão antecipada:</strong> se você concluir o curso antes do vencimento, os dois certificados
                            ficam válidos ao mesmo tempo até o anterior vencer.
                        </li>
                    </ul>
                    <div className="alert alert-warning mt-3 mb-0">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>O que fazer?</strong> Para obter um novo certificado válido, conclua o curso liberado para reciclagem em <strong>Meus Cursos</strong>.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-primary" onClick={() => setShowModalInfoExpirado(false)}>
                        Entendi
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default Certificado;
